import { apiRequest } from '@/services/httpClient';
import { ApiError, unwrapEnvelope } from '@/services/api/helpers';

async function envelope(path, data = {}) {
  const response = await apiRequest('post', path, { data });
  return unwrapEnvelope(response.data);
}

function responseContentType(response) {
  return [
    response?.data?.type,
    response?.headers?.['content-type'],
    response?.headers?.get?.('content-type'),
  ].filter(Boolean).join(';');
}

function readBlobText(blob) {
  if (typeof blob.text === 'function') return blob.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}

async function throwJsonExportError(response, fallback, cause = null) {
  const blob = response?.data;
  if (!(blob instanceof Blob) || !responseContentType(response).toLowerCase().includes('json')) return false;

  try {
    const value = JSON.parse(await readBlobText(blob));
    if (value?.isSuccess === false) {
      throw new ApiError(value.message || fallback, value.data, response?.status);
    }
    throw new ApiError(value?.message || fallback, value?.data, response?.status);
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError(fallback, null, response?.status || cause?.response?.status);
  }
}

async function exportFile(path, data) {
  const fallback = 'The export could not be created.';
  try {
    const response = await apiRequest('post', path, { data, responseType: 'blob' });
    await throwJsonExportError(response, fallback);
    return response;
  } catch (error) {
    await throwJsonExportError(error?.response, fallback, error);
    throw error;
  }
}

export function attendanceExportFileName(filter = {}) {
  const startDate = /^\d{4}-\d{2}-\d{2}$/.test(filter.startDate || '') ? filter.startDate : 'start';
  const endDate = /^\d{4}-\d{2}-\d{2}$/.test(filter.endDate || '') ? filter.endDate : 'end';
  return `ClockSpotter-Attendance-${startDate}-to-${endDate}.xlsx`;
}

export async function exportAttendanceOnce(state, filter) {
  if (state.exporting) return null;
  state.exporting = true;
  try {
    return await attendanceService.exportExcel(filter);
  } finally {
    state.exporting = false;
  }
}

async function fileResponse(path, data) {
  try {
    return await exportFile(path, data);
  } catch (error) {
    const blob = error?.response?.data;
    if (blob instanceof Blob && blob.type?.includes('json')) {
      try {
        const value = JSON.parse(await blob.text());
        throw new ApiError(value?.message || 'The file could not be loaded.', value?.data, error.response.status);
      } catch (parseError) {
        if (parseError instanceof ApiError) throw parseError;
      }
    }
    throw error;
  }
}

function manualAttendancePayload(value) {
  return {
    ...(value.attendanceId ? { attendanceId: value.attendanceId } : {}),
    employeeId: value.employeeId,
    checkedTime: value.checkedTime,
    isCheckout: value.isCheckout ?? value.isCheckedout ?? false,
    isOvertimeApproved: (value.isCheckout ?? value.isCheckedout ?? false) && value.isOvertimeApproved === true,
    restHolidayCompensationMode: (value.isCheckout ?? value.isCheckedout ?? false) && value.isOvertimeApproved
      ? value.restHolidayCompensationMode || null : null,
    compensatoryRestDate: value.restHolidayCompensationMode === 'SubstituteRest'
      ? value.compensatoryRestDate || null : null,
    scheduleId: value.scheduleId || null,
    locationId: value.locationId || null,
    location: value.location || null,
    reason: value.reason || value.adjustmentReason,
    ...(value.clientRequestId ? { clientRequestId: value.clientRequestId } : {}),
    ...(value.rowVersion ? { rowVersion: value.rowVersion } : {}),
  };
}

function attendancePairPayload(value) {
  return {
    employeeId: value.employeeId,
    checkInTime: value.checkInTime,
    checkOutTime: value.checkOutTime,
    isOvertimeApproved: value.isOvertimeApproved === true,
    restHolidayCompensationMode: value.isOvertimeApproved
      ? value.restHolidayCompensationMode || null : null,
    compensatoryRestDate: value.isOvertimeApproved && value.restHolidayCompensationMode === 'SubstituteRest'
      ? value.compensatoryRestDate || null : null,
    scheduleId: value.scheduleId || null,
    locationId: value.locationId || null,
    location: value.location || null,
    reason: value.reason || value.adjustmentReason,
    clientRequestId: value.clientRequestId,
  };
}

export const dashboardService = {
  // Intentionally the one legacy plain-object response used by the employer portal.
  async getToday() {
    const response = await apiRequest('post', '/Attendance/GetDetailedAttendanceSummaryToday');
    const value = response.data;
    if (!value || typeof value !== 'object' || typeof value.totalEmployees !== 'number') {
      throw new ApiError('The dashboard returned an invalid response.');
    }
    return value;
  },
};

export const hrDashboardService = {
  get: (filter = null) => envelope('/Employer/GetHrDashboard', typeof filter === 'string' || filter == null ? { asOfDate: filter } : filter),
  getDetails: (filter) => envelope('/Employer/GetHrDashboardDetails', filter),
};

export const hrPolicyService = {
  get: () => envelope('/Employer/GetMyHrPolicy'),
  save: (policy) => envelope('/Employer/SaveMyHrPolicy', policy),
};

export const employerCalendarService = {
  get: (year) => envelope('/EmployerCalendar/GetMyCalendar', { year }),
  save: (day) => envelope('/EmployerCalendar/SaveMyCalendarDay', day),
  remove: (employerCalendarDayId, rowVersion) => envelope('/EmployerCalendar/DeleteMyCalendarDay', { employerCalendarDayId, rowVersion }),
};

export const payrollService = {
  generate: (year, month) => envelope('/Salary/GenerateEmployerMonthlyPayroll', { year, month }),
  get: (year, month) => envelope('/Salary/GetEmployerMonthlyPayroll', { year, month }),
  recalculate: (payrollRunId, rowVersion) => envelope('/Salary/RecalculateEmployerMonthlyPayroll', { payrollRunId, rowVersion }),
  approve: (payrollRunId, rowVersion) => envelope('/Salary/ApproveEmployerMonthlyPayroll', { payrollRunId, rowVersion }),
  export: (payrollRunId) => exportFile('/Salary/ExportEmployerMonthlyPayroll', { payrollRunId }),
};
export const employeeService = {
  getMany: (filter) => envelope('/Employee/GetEmployerEmployees', filter),
  async getAllForSelect(status = null) {
    const employees = [];
    let pageIndex = 1;
    const pageSize = 200;

    let hasMore = true;
    while (hasMore) {
      const request = { pageIndex, pageSize };
      if (status) request.status = status;
      const page = await envelope('/Employee/GetEmployerEmployees', request);
      const items = Array.isArray(page?.items) ? page.items : [];
      employees.push(...items);
      const totalItems = Number(page?.totalItems || employees.length);
      hasMore = items.length > 0 && employees.length < totalItems && items.length === pageSize;
      if (hasMore) pageIndex += 1;
    }

    return employees;
  },
  getOne: (employeeId) => envelope('/Employee/GetEmployerEmployee', { employeeId }),
  save: (employee) => envelope('/Employee/SaveEmployerEmployee', employee),
  remove: (employeeId) => envelope('/Employee/DeleteEmployerEmployee', { employeeId }),
  setActive: (employeeId, isActive, rowVersion = null) => {
    const request = { employeeId, isActive };
    if (rowVersion) request.rowVersion = rowVersion;
    return envelope('/Employee/SetEmployerEmployeeActive', request);
  },
  async getProfilePicture(employeeId) {
    const response = await fileResponse('/Employee/GetEmployerEmployeeProfilePicture', { EmployeeId: employeeId });
    return response.data;
  },
  async uploadProfilePicture(employeeId, picture) {
    const data = new FormData();
    data.append('EmployeeId', employeeId);
    data.append('Picture', picture);
    const response = await apiRequest('post', '/Employee/UploadEmployerEmployeeProfilePicture', { data });
    return unwrapEnvelope(response.data);
  },
  deleteProfilePicture: (employeeId) => envelope('/Employee/DeleteEmployerEmployeeProfilePicture', { EmployeeId: employeeId }),
  getVisaBranches: (includeInactive = false) => envelope('/Employee/GetEmployerVisaBranches', { includeInactive }),
  saveVisaBranch: (visaBranch) => envelope('/Employee/SaveEmployerVisaBranch', visaBranch),
  deactivateVisaBranch: (visaBranchId, rowVersion) => envelope('/Employee/DeactivateEmployerVisaBranch', { visaBranchId, rowVersion }),
  resetPassword: (employeeId, newPassword, rowVersion) => envelope('/Employee/ResetEmployerEmployeePassword', { employeeId, newPassword, rowVersion }),
};

export const employerProfileService = {
  getMine: () => envelope('/Employer/GetMyProfile'),
  updateMine: (profile) => envelope('/Employer/UpdateMyProfile', profile),
};

export const attendanceSecurityService = {
  getMine: () => envelope('/Employer/GetAttendanceSecuritySettings'),
  updateMine: (settings) => envelope('/Employer/UpdateAttendanceSecuritySettings', settings),
};

export const integrationSettingsService = {
  getMine: () => envelope('/Employer/GetIntegrationSettings'),
  saveBioTime: (settings) => envelope('/Employer/SaveBioTimeIntegration', settings),
  testBioTime: () => envelope('/Employer/TestBioTimeConnection'),
  syncBioTimeNow: () => envelope('/Employer/RunBioTimeSyncNow'),
  disableBioTime: (rowVersion) => envelope('/Employer/DisableBioTimeIntegration', { rowVersion }),
  saveSmtp: (settings) => envelope('/Employer/SaveSmtpIntegration', settings),
  testSmtp: () => envelope('/Employer/TestSmtpIntegration'),
  disableSmtp: (rowVersion) => envelope('/Employer/DisableSmtpIntegration', { rowVersion }),
};

export const pendingRequestService = {
  getSummary: () => envelope('/Employer/GetPendingRequestSummary'),
};

export const emiratisationService = {
  getSummary: () => envelope('/Employer/GetEmiratisationComplianceSummary'),
  getSettings: () => envelope('/Employer/GetEmiratisationSettings'),
  updateSettings: (settings) => envelope('/Employer/UpdateEmiratisationSettings', settings),
  getClassifications: () => envelope('/Employer/GetEmployeeEmiratisationClassifications'),
  updateClassification: (classification) => envelope('/Employer/UpdateEmployeeEmiratisationClassification', classification),
};

export const compensationService = {
  getForEmployee: (employeeId) => envelope('/Salary/GetEmployerEmployeeCompensation', { employeeId }),
  save: (compensation) => envelope('/Salary/SaveEmployerEmployeeCompensation', compensation),
  end: (salaryId, endingDate, rowVersion) => envelope('/Salary/EndEmployerEmployeeCompensation', { salaryId, endingDate, rowVersion }),
  addChange: (change) => envelope('/Salary/AddEmployerEmployeeSalaryChange', change),
  updateFuture: (change) => envelope('/Salary/UpdateFutureEmployerEmployeeSalary', change),
  removeFuture: (salaryId, rowVersion) => envelope('/Salary/DeleteFutureEmployerEmployeeSalary', { salaryId, rowVersion }),
  getEndOfServicePreview: (employeeId, serviceEndDate) => envelope('/Salary/GetEndOfServicePreview', { employeeId, serviceEndDate }),
};

export const workPatternService = {
  getDefault: () => envelope('/Schedule/GetEmployerDefaultWorkPattern'),
  getForEmployee: (employeeId) => envelope('/Schedule/GetEmployerEmployeeWorkPattern', { employeeId }),
  getHistory: (employeeId) => envelope('/Schedule/GetEmployerEmployeeWorkPatterns', { employeeId }),
  getCalendar: (employeeId, startDate, endDate) => envelope('/Schedule/GetEmployerEmployeeScheduleCalendar', { employeeId, startDate, endDate }),
  save: (workPattern) => envelope('/Schedule/SaveEmployerEmployeeWorkPattern', workPattern),
  deactivate: (employeeWorkPatternId, rowVersion) => envelope('/Schedule/DeactivateEmployerEmployeeWorkPattern', { employeeWorkPatternId, rowVersion }),
};

export const deductionService = {
  getForEmployee: (employeeId, status = 'All') => envelope('/EmployeeDeduction/GetEmployerEmployeeDeductions', { employeeId, status }),
  async save(request) {
    const data = new FormData();
    Object.entries(request).forEach(([key, value]) => {
      if (value !== undefined && value !== null && key !== 'document') data.append(key, value);
    });
    if (request.document) data.append('Document', request.document);
    const response = await apiRequest('post', '/EmployeeDeduction/SaveEmployerEmployeeDeduction', { data });
    return unwrapEnvelope(response.data);
  },
  cancel: (employeeDeductionId, rowVersion, reason) => envelope('/EmployeeDeduction/CancelEmployerEmployeeDeduction', { employeeDeductionId, rowVersion, reason }),
  getDocument: (employeeDeductionId) => fileResponse('/EmployeeDeduction/GetEmployerEmployeeDeductionDocument', { employeeDeductionId }),
};

export const employeeDocumentService = {
  getForEmployee: (employeeId, includeArchived = false) => envelope('/EmployeeDocument/GetEmployerEmployeeDocuments', { employeeId, includeArchived }),
  async upload(request) {
    const data = new FormData();
    data.append('EmployeeId', request.employeeId);
    data.append('DocumentName', request.documentName);
    data.append('EmployeeVisible', String(request.employeeVisible === true));
    if (request.expiryDate) data.append('ExpiryDate', request.expiryDate);
    data.append('Document', request.document);
    const response = await apiRequest('post', '/EmployeeDocument/UploadEmployerEmployeeDocument', { data });
    return unwrapEnvelope(response.data);
  },
  update: (request) => envelope('/EmployeeDocument/UpdateEmployerEmployeeDocument', request),
  async replace(employeeDocumentId, rowVersion, document) {
    const data = new FormData();
    data.append('EmployeeDocumentId', employeeDocumentId);
    if (rowVersion) data.append('RowVersion', rowVersion);
    data.append('Document', document);
    const response = await apiRequest('post', '/EmployeeDocument/ReplaceEmployerEmployeeDocument', { data });
    return unwrapEnvelope(response.data);
  },
  archive: (employeeDocumentId, rowVersion) => envelope('/EmployeeDocument/ArchiveEmployerEmployeeDocument', { employeeDocumentId, rowVersion }),
  getDocument: (employeeDocumentId) => fileResponse('/EmployeeDocument/GetEmployerEmployeeDocument', { employeeDocumentId }),
  getExpirySummary: (limit = 8) => envelope('/EmployeeDocument/GetEmployerEmployeeDocumentExpirySummary', { limit }),
};

export const holidayService = {
  getForEmployee: (employeeId, year = null) => envelope('/Holiday/GetEmployerEmployeeHolidays', { employeeId, year }),
  save: (holiday) => envelope('/Holiday/SaveEmployerEmployeeHoliday', holiday),
  remove: (holidayId, rowVersion) => envelope('/Holiday/DeleteEmployerEmployeeHoliday', { holidayId, rowVersion }),
  getTypes: () => envelope('/Holiday/GetHolidayTypes'),
};

export const memoService = {
  getMany: (scope = 'active') => envelope('/Memo/GetEmployerMemos', { scope }),
  save: (memo) => envelope('/Memo/SaveEmployerMemo', memo),
  remove: (memoId, rowVersion) => envelope('/Memo/DeleteEmployerMemo', { memoId, rowVersion }),
};

export const attendanceService = {
  getMany: (filter) => envelope('/Attendance/GetEmployerAttendance', filter),
  getOne: (attendanceId) => envelope('/Attendance/GetEmployerAttendanceDetail', { attendanceId }),
  getSummary: (filter) => envelope('/Attendance/GetEmployerAttendanceSummary', filter),
  create: (attendance) => envelope('/Attendance/CreateEmployerAttendance', manualAttendancePayload(attendance)),
  createPair: (attendance) => envelope('/Attendance/CreateEmployerAttendancePair', attendancePairPayload(attendance)),
  update: (attendance) => envelope('/Attendance/UpdateEmployerAttendance', manualAttendancePayload(attendance)),
  void: (attendanceId, reason, rowVersion = null) => envelope('/Attendance/VoidEmployerAttendance', { attendanceId, reason, rowVersion }),
  getAdjustments: (attendanceId) => envelope('/Attendance/GetEmployerAttendanceAdjustments', { attendanceId }),
  exportExcel: (filter) => exportFile('/Attendance/ExportEmployerAttendance', filter),
  async getImage(attendanceId) {
    const response = await fileResponse('/Attendance/GetEmployerAttendanceImage', { AttendanceId: attendanceId });
    return response.data;
  },
};

export const vacationService = {
  getMany: (filter) => envelope('/Vacation/GetEmployerVacationApprovals', filter),
  getTypes: (employeeId = null) => envelope('/Vacation/GetEmployerVacationTypes', employeeId ? { employeeId } : {}),
  async createForEmployee(request) {
    const data = new FormData();
    data.append('EmployeeId', request.employeeId);
    data.append('StartDate', request.startDate);
    data.append('EndDate', request.endDate);
    data.append('Reason', request.reason || '');
    data.append('VacationTypeId', request.vacationTypeId);
    if (request.handoverPersonName?.trim()) data.append('HandoverPersonName', request.handoverPersonName.trim());
    if (request.qualifyingEventDate) data.append('QualifyingEventDate', request.qualifyingEventDate);
    if (request.qualifyingRelationship) data.append('QualifyingRelationship', request.qualifyingRelationship);
    if (request.evidenceReference) data.append('EvidenceReference', request.evidenceReference);
    if (request.document) data.append('Document', request.document);
    const response = await apiRequest('post', '/Vacation/CreateEmployerVacation', { data });
    return unwrapEnvelope(response.data);
  },
  approve: (approvalId) => envelope('/Vacation/ApproveEmployerVacation', { approvalId }),
  reject: (approvalId) => envelope('/Vacation/RejectEmployerVacation', { approvalId }),
  cancel: (approvalId) => envelope('/Vacation/CancelEmployerVacation', { approvalId }),
  getDocument: (approvalId) => fileResponse('/Vacation/GetEmployerVacationDocument', { approvalId }),
};

export const leaveBalanceService = {
  getForEmployee: (employeeId) => envelope('/Vacation/GetEmployerEmployeeLeaveBalances', { employeeId }),
  getHistoricalUsage: (employeeId) => envelope('/Vacation/GetEmployerEmployeeHistoricalLeaveUsage', { employeeId }),
  saveHistoricalUsage: (usage) => envelope('/Vacation/SaveEmployerEmployeeHistoricalLeaveUsage', usage),
  removeHistoricalUsage: (employeeLeaveHistoricalUsageId, rowVersion) => envelope('/Vacation/DeleteEmployerEmployeeHistoricalLeaveUsage', { employeeLeaveHistoricalUsageId, rowVersion }),
};

export const letterRequestService = {
  getMany: (filter) => envelope('/EmployeeLetter/GetEmployerLetterRequests', filter),
  getTypes: () => envelope('/EmployeeLetter/GetEmployerLetterTypes'),
  create: (request) => envelope('/EmployeeLetter/CreateEmployerLetterRequest', request),
  update: (request) => envelope('/EmployeeLetter/UpdateEmployerLetterRequest', request),
  cancel: (letterRequestId, rowVersion, reason) => envelope('/EmployeeLetter/CancelEmployerLetterRequest', { letterRequestId, rowVersion, reason }),
  async complete(letterRequestId, rowVersion, document) {
    const data = new FormData();
    data.append('LetterRequestId', letterRequestId);
    if (rowVersion) data.append('RowVersion', rowVersion);
    data.append('Document', document);
    const response = await apiRequest('post', '/EmployeeLetter/CompleteEmployerLetterRequest', { data });
    return unwrapEnvelope(response.data);
  },
  reject: (letterRequestId, rowVersion, rejectionReason) => envelope('/EmployeeLetter/RejectEmployerLetterRequest', { letterRequestId, rowVersion, rejectionReason }),
  getDocument: (letterRequestId) => fileResponse('/EmployeeLetter/GetEmployerLetterDocument', { letterRequestId }),
};

export const taskService = {
  getMany: (filter) => envelope('/Task/GetEmployerTasks', filter),
  assign: (task) => envelope('/Task/AssignEmployerTask', task),
  update: (task) => envelope('/Task/UpdateEmployerTask', task),
  remove: (employeeTaskId) => envelope('/Task/DeleteEmployerTask', { employeeTaskId }),
  complete: (employeeTaskId) => envelope('/Task/CompleteEmployerTask', { employeeTaskId }),
};

export const locationService = {
  getMany: () => envelope('/Location/GetEmployerLocations'),
  create: (location) => envelope('/Location/AddEmployerLocation', location),
  update: (location) => envelope('/Location/UpdateEmployerLocation', location),
  remove: (locationId, rowVersion) => envelope('/Location/DeleteEmployerLocation', { locationId, rowVersion }),
};

export const referenceService = {
  getDepartments: () => envelope('/Department/GetDepartments'),
  getSalaryTypes: () => envelope('/SalaryType/GetSalaryType'),
  getTaskTypes: () => envelope('/Task/GetTaskType'),
};

export default {
  dashboardService,
  hrDashboardService,
  hrPolicyService,
  payrollService,
  employeeService,
  employerProfileService,
  attendanceSecurityService,
  emiratisationService,
  compensationService,
  deductionService,
  employeeDocumentService,
  workPatternService,
  holidayService,
  memoService,
  attendanceService,
  vacationService,
  leaveBalanceService,
  letterRequestService,
  taskService,
  locationService,
  referenceService,
};
